import firebase from "firebase/app";

export interface UserAuditLog {
  id?: string;
  uid?: string;
  account_key?: string;
  user_id?: string;
  user_email?: string;
  user_name?: string;
  user_role?: string;

  // Action details
  action_type?: AuditActionType;
  action_description?: string;
  resource_type?: string;
  resource_id?: string;
  resource_name?: string;

  // Data changes (for update operations)
  old_values?: any;
  new_values?: any;
  changes_summary?: string;

  // Request context
  ip_address?: string;
  user_agent?: string;
  session_id?: string;
  route_path?: string;

  // Timestamps
  timestamp?: firebase.firestore.Timestamp;
  date_created?: Date;

  // Additional metadata
  status?: AuditStatus;
  severity?: AuditSeverity;
  category?: AuditCategory;
  tags?: string[];
  notes?: string;
}

export enum AuditActionType {
  // Authentication actions
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  LOGIN_FAILED = 'LOGIN_FAILED',
  PASSWORD_CHANGE = 'PASSWORD_CHANGE',

  // CRUD operations
  CREATE = 'CREATE',
  READ = 'READ',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',

  // Data operations
  EXPORT = 'EXPORT',
  IMPORT = 'IMPORT',
  BULK_UPDATE = 'BULK_UPDATE',
  BULK_DELETE = 'BULK_DELETE',

  // Navigation
  PAGE_VIEW = 'PAGE_VIEW',
  NAVIGATION = 'NAVIGATION',

  // System operations
  SETTINGS_CHANGE = 'SETTINGS_CHANGE',
  PERMISSION_CHANGE = 'PERMISSION_CHANGE',
  ROLE_ASSIGNMENT = 'ROLE_ASSIGNMENT',

  // Parking operations
  VEHICLE_ENTRY = 'VEHICLE_ENTRY',
  VEHICLE_EXIT = 'VEHICLE_EXIT',
  PAYMENT_PROCESS = 'PAYMENT_PROCESS',
  RATE_CALCULATION = 'RATE_CALCULATION',

  // Account management
  ACCOUNT_CREATE = 'ACCOUNT_CREATE',
  ACCOUNT_UPDATE = 'ACCOUNT_UPDATE',
  ACCOUNT_DELETE = 'ACCOUNT_DELETE',
  ACCOUNT_SUSPEND = 'ACCOUNT_SUSPEND',
  ACCOUNT_ACTIVATE = 'ACCOUNT_ACTIVATE',

  // User management
  USER_CREATE = 'USER_CREATE',
  USER_UPDATE = 'USER_UPDATE',
  USER_DELETE = 'USER_DELETE',
  USER_ROLE_CHANGE = 'USER_ROLE_CHANGE',

  // Zone and parking management
  ZONE_CREATE = 'ZONE_CREATE',
  ZONE_UPDATE = 'ZONE_UPDATE',
  ZONE_DELETE = 'ZONE_DELETE',
  SLOT_ASSIGN = 'SLOT_ASSIGN',
  SLOT_UNASSIGN = 'SLOT_UNASSIGN',

  // Reports and analytics
  REPORT_GENERATE = 'REPORT_GENERATE',
  REPORT_EXPORT = 'REPORT_EXPORT',
  DATA_ANALYSIS = 'DATA_ANALYSIS',

  // Employee management
  EMPLOYEE_CREATE = 'EMPLOYEE_CREATE',
  EMPLOYEE_UPDATE = 'EMPLOYEE_UPDATE',
  EMPLOYEE_DELETE = 'EMPLOYEE_DELETE',

  // Vehicle management
  VEHICLE_REGISTER = 'VEHICLE_REGISTER',
  VEHICLE_UPDATE = 'VEHICLE_UPDATE',
  VEHICLE_DELETE = 'VEHICLE_DELETE'
}

export enum AuditStatus {
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  PENDING = 'PENDING',
  CANCELLED = 'CANCELLED'
}

export enum AuditSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL'
}

export enum AuditCategory {
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  DATA_MODIFICATION = 'DATA_MODIFICATION',
  SYSTEM_ACCESS = 'SYSTEM_ACCESS',
  FINANCIAL = 'FINANCIAL',
  SECURITY = 'SECURITY',
  ADMINISTRATION = 'ADMINISTRATION',
  OPERATIONS = 'OPERATIONS',
  COMPLIANCE = 'COMPLIANCE'
}

export interface AuditLogFilter {
  start_date?: Date;
  end_date?: Date;
  user_id?: string;
  account_key?: string;
  action_types?: AuditActionType[];
  resource_types?: string[];
  categories?: AuditCategory[];
  severity?: AuditSeverity[];
  status?: AuditStatus[];
  search_term?: string;
  limit?: number;
  offset?: number;
  lastDocId?: string; // For cursor-based pagination
}

// Paginated result interface for audit logs
export interface PaginatedAuditLogs {
  logs: UserAuditLog[];
  lastDocId: string | null;
  hasMore: boolean;
  totalFetched: number;
}

export interface AuditLogSummary {
  total_logs?: number;
  date_range?: {
    start: Date;
    end: Date;
  };
  top_actions?: Array<{
    action: AuditActionType;
    count: number;
  }>;
  top_users?: Array<{
    user_name: string;
    user_email: string;
    action_count: number;
  }>;
  security_events?: number;
  failed_attempts?: number;
  data_modifications?: number;
}

// Enhanced User Activity Models
export interface UserActivitySession {
  id?: string;
  user_id?: string;
  user_email?: string;
  account_key?: string;
  session_id?: string;
  start_time?: firebase.firestore.Timestamp;
  end_time?: firebase.firestore.Timestamp;
  duration_minutes?: number;
  ip_address?: string;
  user_agent?: string;
  device_type?: DeviceType;
  browser?: string;
  os?: string;
  location?: GeoLocation;
  activities?: UserActivity[];
  pages_visited?: PageVisit[];
  total_actions?: number;
  last_activity?: firebase.firestore.Timestamp;
  is_active?: boolean;
}

export interface UserActivity {
  id?: string;
  session_id?: string;
  timestamp?: firebase.firestore.Timestamp;
  action_type?: ActivityType;
  action_details?: string;
  resource_accessed?: string;
  time_spent_seconds?: number;
  mouse_clicks?: number;
  keyboard_strokes?: number;
  scroll_distance?: number;
  form_interactions?: FormInteraction[];
  api_calls?: ApiCall[];
}

export interface PageVisit {
  id?: string;
  session_id?: string;
  page_url?: string;
  page_title?: string;
  entry_time?: firebase.firestore.Timestamp;
  exit_time?: firebase.firestore.Timestamp;
  time_spent_seconds?: number;
  scroll_depth_percentage?: number;
  bounce?: boolean;
  previous_page?: string;
  next_page?: string;
  interactions?: PageInteraction[];
}

export interface FormInteraction {
  form_id?: string;
  form_name?: string;
  field_name?: string;
  interaction_type?: FormInteractionType;
  timestamp?: firebase.firestore.Timestamp;
  value_changed?: boolean;
}

export interface ApiCall {
  endpoint?: string;
  method?: string;
  timestamp?: firebase.firestore.Timestamp;
  response_time_ms?: number;
  status_code?: number;
  data_size_bytes?: number;
}

export interface PageInteraction {
  element_id?: string;
  element_type?: string;
  interaction_type?: InteractionType;
  timestamp?: firebase.firestore.Timestamp;
  coordinates?: { x: number, y: number };
}

export interface GeoLocation {
  country?: string;
  region?: string;
  city?: string;
  timezone?: string;
  latitude?: number;
  longitude?: number;
}

export interface UserBehaviorAnalytics {
  user_id?: string;
  account_key?: string;
  analysis_period?: {
    start: Date;
    end: Date;
  };
  total_sessions?: number;
  total_time_minutes?: number;
  average_session_duration?: number;
  most_active_hours?: number[];
  most_active_days?: string[];
  favorite_pages?: Array<{
    page: string;
    visits: number;
    avg_time: number;
  }>;
  common_actions?: Array<{
    action: string;
    frequency: number;
  }>;
  productivity_score?: number;
  engagement_level?: EngagementLevel;
  risk_indicators?: RiskIndicator[];
}

export interface ActivityDashboardData {
  summary?: {
    active_users?: number;
    total_sessions?: number;
    total_activities?: number;
    avg_session_duration?: number;
  };
  real_time_activities?: UserActivity[];
  hourly_activity?: Array<{
    hour: number;
    activity_count: number;
  }>;
  daily_trends?: Array<{
    date: string;
    sessions: number;
    activities: number;
  }>;
  top_pages?: Array<{
    page: string;
    visits: number;
    unique_visitors: number;
  }>;
  user_engagement?: Array<{
    user_email: string;
    engagement_score: number;
    last_activity: Date;
  }>;
  security_events?: SecurityEvent[];
}

export interface SecurityEvent {
  id?: string;
  user_id?: string;
  event_type?: SecurityEventType;
  severity?: SecuritySeverity;
  description?: string;
  timestamp?: firebase.firestore.Timestamp;
  ip_address?: string;
  user_agent?: string;
  resolved?: boolean;
  risk_score?: number;
}

// Enhanced Enums
export enum ActivityType {
  CLICK = 'CLICK',
  SCROLL = 'SCROLL',
  HOVER = 'HOVER',
  FOCUS = 'FOCUS',
  BLUR = 'BLUR',
  FORM_SUBMIT = 'FORM_SUBMIT',
  FORM_INPUT = 'FORM_INPUT',
  DOWNLOAD = 'DOWNLOAD',
  UPLOAD = 'UPLOAD',
  SEARCH = 'SEARCH',
  FILTER = 'FILTER',
  SORT = 'SORT',
  PRINT = 'PRINT',
  SHARE = 'SHARE',
  BOOKMARK = 'BOOKMARK',
  COPY = 'COPY',
  PASTE = 'PASTE',
  SELECT = 'SELECT',
  DRAG = 'DRAG',
  DROP = 'DROP',
  RESIZE = 'RESIZE',
  MINIMIZE = 'MINIMIZE',
  MAXIMIZE = 'MAXIMIZE'
}

export enum DeviceType {
  DESKTOP = 'DESKTOP',
  LAPTOP = 'LAPTOP',
  TABLET = 'TABLET',
  MOBILE = 'MOBILE',
  UNKNOWN = 'UNKNOWN'
}

export enum FormInteractionType {
  FOCUS = 'FOCUS',
  BLUR = 'BLUR',
  INPUT = 'INPUT',
  SELECT = 'SELECT',
  SUBMIT = 'SUBMIT',
  RESET = 'RESET',
  VALIDATION_ERROR = 'VALIDATION_ERROR'
}

export enum InteractionType {
  CLICK = 'CLICK',
  RIGHT_CLICK = 'RIGHT_CLICK',
  DOUBLE_CLICK = 'DOUBLE_CLICK',
  HOVER = 'HOVER',
  FOCUS = 'FOCUS',
  BLUR = 'BLUR',
  SCROLL = 'SCROLL',
  RESIZE = 'RESIZE',
  DRAG_START = 'DRAG_START',
  DRAG_END = 'DRAG_END'
}

export enum EngagementLevel {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  VERY_HIGH = 'VERY_HIGH'
}

export enum SecurityEventType {
  SUSPICIOUS_LOGIN = 'SUSPICIOUS_LOGIN',
  MULTIPLE_FAILED_ATTEMPTS = 'MULTIPLE_FAILED_ATTEMPTS',
  UNUSUAL_LOCATION = 'UNUSUAL_LOCATION',
  SESSION_HIJACKING = 'SESSION_HIJACKING',
  PRIVILEGE_ESCALATION = 'PRIVILEGE_ESCALATION',
  DATA_EXFILTRATION = 'DATA_EXFILTRATION',
  BRUTE_FORCE = 'BRUTE_FORCE',
  ANOMALOUS_BEHAVIOR = 'ANOMALOUS_BEHAVIOR'
}

export enum SecuritySeverity {
  INFO = 'INFO',
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL'
}

export interface RiskIndicator {
  type: string;
  description: string;
  score: number;
  timestamp: Date;
}

// Zones Slot Activity Models
export interface ZonesSlotActivity {
  id?: string;
  account_key?: string;
  action_type?: number;
  log_id?: string;
  previous_log_id?: string;
  slot_id?: string;
  slot_name?: string;
  timestamp?: firebase.firestore.Timestamp;
  updated_by_id?: string;
  updated_by_name?: string;
  zone_id?: string;
  zone_name?: string;
}

export interface ZonesSlotActivityFilter {
  start_date?: Date;
  end_date?: Date;
  zone_id?: string;
  zone_name?: string;
  slot_id?: string;
  slot_name?: string;
  action_type?: number;
  account_key?: string;
  search_term?: string;
  limit?: number;
  offset?: number;
  lastDocId?: string; // For cursor-based pagination
}

// Paginated result interface for zone slot activities
export interface PaginatedZonesSlotActivities {
  activities: ZonesSlotActivity[];
  lastDocId: string | null;
  hasMore: boolean;
  totalFetched: number;
}

export interface ZonesSlotActivitySummary {
  total_activities?: number;
  unique_zones?: number;
  unique_slots?: number;
  action_types?: Array<{
    action_type: number;
    count: number;
  }>;
  zone_breakdown?: Array<{
    zone_id: string;
    zone_name: string;
    activity_count: number;
    last_activity: firebase.firestore.Timestamp;
  }>;
}

export enum SlotStatus {
  AVAILABLE = 'available',
  OCCUPIED = 'occupied',
  MAINTENANCE = 'maintenance',
  RESERVED = 'reserved',
  OUT_OF_ORDER = 'out_of_order'
}


// Zone Slot
export interface ZoneSlot {
  account_key?: string
  allowanceTimeDuration?: boolean
  allowanceTimerActive?:boolean
  dateCreated?: Date
  endTime?: Date
  isExpired?: boolean
  log_id?: string
  slot_arrangement?:number
  slot_code?: string
  slot_id?:string
  slot_name?: string
  status?: number
  updatedAt?: Date
  zone_id?: string
}
